import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useBadges } from "@/hooks/useBadges";
import { UserRole } from "@/lib/schema";
import { 
  Menu,
  X,
  Brain,
  List,
  Plus,
  UserCog,
  UserCheck,
  Crown,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
  Briefcase,
  FileWarning,
  Sparkles,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Import SVG icons
import dashboardIcon from "@/assets/dashboard/sidebar/dashboard.svg";
import referralsIcon from "@/assets/dashboard/sidebar/referrals.svg";
import jobsIcon from "@/assets/dashboard/sidebar/jobs.svg";
import rewardsIcon from "@/assets/dashboard/sidebar/rewards.svg";
import logoutIcon from "@/assets/dashboard/sidebar/logout.svg";
import clientReferralIcon from "@/assets/dashboard/sidebar/client_referral.svg";
import candidateIcon from "@/assets/dashboard/icon_interviewing.svg";
import logo from "/logo.png";

interface SidebarSubItem {
  icon: string | React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  badge?: string | number;
  isCustomIcon?: boolean;
}

interface SidebarItem {
  icon: string | React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  active?: boolean;
  badge?: string | number;
  isCustomIcon?: boolean;
  hasSubmenu?: boolean;
  submenuItems?: SidebarSubItem[];
}

export function SuperAdminSidebar() {
  const [location] = useLocation();
  const { userData, logout } = useAuth();
  const { badges } = useBadges();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Function to determine which sections should be expanded based on current location
  const getExpandedSections = () => {
    const expanded = new Set<string>();
    
    // Only expand the section that matches the current location
    if (location.startsWith('/admin/jobs')) {
      expanded.add('job-hub');
    } else if (location.startsWith('/admin/referrals')) {
      expanded.add('referral-hub');
    } else if (location.startsWith('/admin/settings')) {
      expanded.add('settings');
    } else {
      // Default: only expand Job Hub when on dashboard or other pages
      expanded.add('referral-hub');
    }
    
    return expanded;
  };

  const [expandedSections, setExpandedSections] = useState<Set<string>>(getExpandedSections());

  // Update expanded sections when location changes
  useEffect(() => {
    const newExpanded = getExpandedSections();
    setExpandedSections(newExpanded);
  }, [location]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const isActive = (href: string) => {
    if (href === "/admin") {
      return location === "/admin";
    }
    
    // Handle /admin/jobs - should be active for /admin/jobs, /admin/jobs/123, etc.
    // but NOT for /admin/jobs/create
    if (href === "/admin/jobs") {
      return location === "/admin/jobs" || 
             (location.startsWith("/admin/jobs/") && !location.startsWith("/admin/jobs/create"));
    }
    
    // For other routes, use startsWith for sub-routes
    return location.startsWith(href);
  };

  // Create role-based sidebar items
  const createSidebarItems = (): SidebarItem[] => {
    const isRecruiter = userData?.role === UserRole.RECRUITER;
    
    const items: SidebarItem[] = [
      {
        icon: dashboardIcon,
        label: "Dashboard",
        href: "/admin",
        active: location === "/admin",
        isCustomIcon: true
      },
      {
        icon: jobsIcon,
        label: "Job Hub",
        active: location.startsWith("/admin/jobs"),
        isCustomIcon: true,
        hasSubmenu: true,
        submenuItems: isRecruiter 
          ? [{ icon: List, label: "All Jobs", href: "/admin/jobs", isCustomIcon: false }] // Recruiters can only view jobs
          : [
              { icon: List, label: "All Jobs", href: "/admin/jobs", isCustomIcon: false },
              { icon: Plus, label: "Create Job", href: "/admin/jobs/create", isCustomIcon: false }
            ]
      },
      {
        icon: referralsIcon,
        label: "Referral Hub", 
        active: location.startsWith("/admin/referrals") || location.startsWith("/admin/client-referral") || location.startsWith("/admin/cv-missing") || location.startsWith("/admin/pending-probation"),
        isCustomIcon: true,
        hasSubmenu: true,
        submenuItems: isRecruiter
          ? [
              { icon: candidateIcon, label: "Candidate", href: "/admin/referrals", badge: badges.referrals || undefined, isCustomIcon: true }
            ]
          : [
              { icon: candidateIcon, label: "Candidate", href: "/admin/referrals", badge: badges.referrals || undefined, isCustomIcon: true },
              { icon: clientReferralIcon, label: "Client Referrals", href: "/admin/client-referral", badge: badges.clientReferrals || undefined, isCustomIcon: true },
              { icon: FileWarning, label: "CV Missing", href: "/admin/cv-missing", badge: badges.cvMissing || undefined, isCustomIcon: false },
              { icon: Clock, label: "Pending Probation", href: "/admin/pending-probation", isCustomIcon: false }
            ]
      },
      {
        icon: Brain,
        label: "Ask Aria",
        href: "/admin/ai/chat",
        active: location === "/admin/ai/chat" || location.startsWith("/admin/ai"),
        isCustomIcon: false
      }
    ];

    // Add Super Admin only items
    if (!isRecruiter) {
      items.push(
         {
           icon: rewardsIcon,
           label: "Rewards Hub",
           href: "/admin/rewards-hub",
           active: location === "/admin/rewards-hub" || location.startsWith("/admin/rewards-hub"),
           badge: badges.rewards || undefined,
           isCustomIcon: true
         },
        {
          icon: UserCog,
          label: "User Hub",
          href: "/admin/users",
          active: location === "/admin/users" || location.startsWith("/admin/users"),
          isCustomIcon: false
        },
        {
          icon: SettingsIcon,
          label: "Settings",
          active: location.startsWith("/admin/settings"),
          isCustomIcon: false,
          hasSubmenu: true,
          submenuItems: [
            { icon: Briefcase, label: "Job Settings", href: "/admin/settings/jobs", isCustomIcon: false },
            { icon: UserCheck, label: "Referral Settings", href: "/admin/settings/referrals", isCustomIcon: false },
            // { icon: Crown, label: "Reward Settings", href: "/admin/settings/rewards", isCustomIcon: false },
            { icon: Sparkles, label: "GraphRAG Tuning", href: "/admin/settings/graphrag", isCustomIcon: false }
          ]
        }
      );
    }

    return items;
  };

  const sidebarItems = createSidebarItems();

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-white border-gray-200"
        >
          {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 
        w-64 lg:w-56 bg-white border-r border-gray-200 h-screen flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center">
            <img 
              src={logo} 
              alt="AirTalent Logo" 
              className="h-7 w-auto object-contain"
            />
          </div>
        </div>

        {/* Menu Label */}
        <div className="p-4">
          <p className="text-gray-400 uppercase font-semibold" style={{
            fontFamily: 'Manrope',
            fontWeight: 600,
            fontSize: '10px',
            lineHeight: '140%',
            letterSpacing: '0%'
          }}>
            MENU
          </p>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {sidebarItems.map((item) => {
            const sectionId = item.label.toLowerCase().replace(' ', '-');
            const isExpanded = expandedSections.has(sectionId);
            
            return (
              <div key={item.href || sectionId}>
                {item.hasSubmenu ? (
                  // Expandable section with submenus
                  <>
                    <div
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors space-grotesk font-medium cursor-pointer ${
                        item.active
                          ? "border-b border-orange-500"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      data-testid={`nav-${sectionId}`}
                      onClick={() => toggleSection(sectionId)}
                    >
                      {item.isCustomIcon ? (
                        <img 
                          src={item.icon as string} 
                          alt={item.label}
                          className={`h-4 w-4 transition-colors ${
                            item.active 
                              ? "brightness-0 saturate-100" 
                              : "opacity-60 hover:opacity-80"
                          }`}
                          style={item.active ? { 
                            filter: "brightness(0) saturate(100%) invert(45%) sepia(69%) saturate(959%) hue-rotate(346deg) brightness(97%) contrast(94%)" 
                          } : {
                            filter: "brightness(0) saturate(100%) invert(53%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(53%) contrast(88%)"
                          }}
                        />
                      ) : (
                        <span style={{ color: item.active ? "#F97316" : "#888888" }}>
                          {React.createElement(item.icon as React.ComponentType<{ className?: string }>, {
                            className: "h-4 w-4 transition-colors"
                          })}
                        </span>
                      )}
                      <span 
                        className="flex-1"
                        style={{
                          fontFamily: 'Manrope',
                          fontWeight: item.active ? 600 : 400,
                          fontSize: '14px',
                          lineHeight: '140%',
                          letterSpacing: '0%',
                          color: item.active ? '#2D2D2D' : '#888888'
                        }}
                      >
                        {item.label}
                      </span>
                      {isExpanded ? 
                        <ChevronDown className="h-4 w-4 text-gray-400" /> : 
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      }
                    </div>

                    {/* Submenu Items */}
                    {isExpanded && item.submenuItems && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.submenuItems.map((subItem) => (
                          <Link key={subItem.href} href={subItem.href}>
                            <div
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                                isActive(subItem.href)
                                  ? "bg-orange-100 text-orange-700"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              }`}
                              data-testid={`nav-${subItem.href.replace(/\//g, '-')}`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {subItem.isCustomIcon ? (
                                <img 
                                  src={subItem.icon as string} 
                                  alt={subItem.label}
                                  className="h-4 w-4 flex-shrink-0"
                                  style={isActive(subItem.href) ? { 
                                    filter: "brightness(0) saturate(100%) invert(45%) sepia(69%) saturate(959%) hue-rotate(346deg) brightness(97%) contrast(94%)" 
                                  } : {
                                    filter: "brightness(0) saturate(100%) invert(53%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(53%) contrast(88%)"
                                  }}
                                />
                              ) : (
                                <span style={{ color: isActive(subItem.href) ? "#F97316" : "#888888" }}>
                                  {React.createElement(subItem.icon as React.ComponentType<{ className?: string }>, {
                                    className: "h-3 w-3 flex-shrink-0 transition-colors"
                                  })}
                                </span>
                              )}
                              <span 
                                className="flex-1"
                                style={{
                                  fontFamily: 'Manrope',
                                  fontWeight: isActive(subItem.href) ? 600 : 400,
                                  fontSize: '13px',
                                  lineHeight: '140%',
                                  letterSpacing: '0%',
                                  color: isActive(subItem.href) ? '#2D2D2D' : '#888888'
                                }}
                              >
                                {subItem.label}
                              </span>
                              {subItem.badge && (
                                <Badge className="bg-orange-500 text-white text-xs space-grotesk">
                                  {subItem.badge}
                                </Badge>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  // Regular navigation item
                  <Link href={item.href!}>
                    <div
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors space-grotesk font-medium cursor-pointer ${
                        item.active
                          ? "border-b border-orange-500"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      data-testid={`nav-${sectionId}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.isCustomIcon ? (
                        <img 
                          src={item.icon as string} 
                          alt={item.label}
                          className={`h-4 w-4 transition-colors ${
                            item.active 
                              ? "brightness-0 saturate-100" 
                              : "opacity-60 hover:opacity-80"
                          }`}
                          style={item.active ? { 
                            filter: "brightness(0) saturate(100%) invert(45%) sepia(69%) saturate(959%) hue-rotate(346deg) brightness(97%) contrast(94%)" 
                          } : {
                            filter: "brightness(0) saturate(100%) invert(53%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(53%) contrast(88%)"
                          }}
                        />
                      ) : (
                        <span style={{ color: item.active ? "#F97316" : "#888888" }}>
                          {React.createElement(item.icon as React.ComponentType<{ className?: string }>, {
                            className: "h-4 w-4 transition-colors"
                          })}
                        </span>
                      )}
                      <span style={{
                        fontFamily: 'Manrope',
                        fontWeight: item.active ? 600 : 400,
                        fontSize: '14px',
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        color: item.active ? '#2D2D2D' : '#888888'
                      }}>{item.label}</span>
                      {item.badge && (
                        <Badge className="bg-orange-500 text-white text-xs space-grotesk">
                          {item.badge}
                        </Badge>
                      )}
                    </div>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200 space-y-1">
          <Button
            variant="ghost"
            onClick={() => {
              handleLogout();
              setIsMobileMenuOpen(false);
            }}
            className="w-full justify-start gap-3 px-3 py-3 text-gray-600 hover:bg-gray-50 space-grotesk font-medium"
            data-testid="button-logout"
          >
            <img 
              src={logoutIcon} 
              alt="Logout"
              className="h-4 w-4 opacity-60 hover:opacity-80 transition-opacity"
            />
            <span style={{
              fontFamily: 'Manrope',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '140%',
              letterSpacing: '0%',
              color: '#888888'
            }}>Logout</span>
          </Button>
        </div>
      </div>
    </>
  );
}